'use client'

import React, { useCallback, useContext, useState, useRef, useEffect, useId } from 'react'
import { useField } from 'formik'
import { Paragraph } from '../../../atoms/Typography/Typography.component'
import { SpacerBase } from '../../../atoms/GridSystem'
import Icon from '../../../atoms/Icon/Icon.component'
import { SectionThemeContext } from '../../../../contexts/SectionTheme.context'
import * as S from './Select.styles'

export type SelectOption = {
  label: string
  value: string
}

type SelectProps = {
  name: string
  id?: string
  label?: string
  helperText?: string
  options: SelectOption[]
  placeholder?: string
  required?: boolean
  'aria-required'?: boolean
  withBackground?: boolean
  disabled?: boolean
}

function Select({ label, helperText, options, placeholder, withBackground, disabled, ...props }: SelectProps) {
  const [field, meta, helpers] = useField(props)
  const { selectedColorScheme } = useContext(SectionThemeContext)
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const autoId = useId()
  const baseId = props.id || autoId
  const listId = `${baseId}-panel`
  const triggerId = props.id || `${autoId}-trigger`
  const hasError = meta.touched && !!meta.error
  const errorId = `${props.name}-error`

  const selectedOption = options.find((opt) => opt.value === field.value)

  const openDropdown = useCallback(() => {
    if (disabled) return
    setIsOpen(true)
    const selectedIdx = options.findIndex((opt) => opt.value === field.value)
    setActiveIndex(selectedIdx >= 0 ? selectedIdx : 0)
  }, [disabled, options, field.value])

  const closeDropdown = useCallback(() => {
    setIsOpen(false)
    setActiveIndex(-1)
  }, [])

  const handleSelect = useCallback((value: string) => {
    helpers.setValue(value)
    closeDropdown()
    triggerRef.current?.focus()
  }, [helpers, closeDropdown])

  const handleTriggerKeyDown = useCallback((event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault()
        if (!isOpen) {
          openDropdown()
        } else {
          setActiveIndex((prev) => {
            if (event.key === 'ArrowDown') return Math.min(prev + 1, options.length - 1)
            return Math.max(prev - 1, 0)
          })
        }
        break
      }
      case 'Enter':
      case ' ': {
        event.preventDefault()
        if (isOpen && activeIndex >= 0) {
          handleSelect(options[activeIndex].value)
        } else {
          openDropdown()
        }
        break
      }
      case 'Home': {
        if (!isOpen) return
        event.preventDefault()
        setActiveIndex(0)
        break
      }
      case 'End': {
        if (!isOpen) return
        event.preventDefault()
        setActiveIndex(options.length - 1)
        break
      }
      case 'Escape': {
        if (!isOpen) return
        event.preventDefault()
        event.stopPropagation()
        closeDropdown()
        triggerRef.current?.focus()
        break
      }
    }
  }, [isOpen, activeIndex, options, openDropdown, closeDropdown, handleSelect])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        closeDropdown()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [closeDropdown])

  useEffect(() => {
    if (!isOpen) return

    function handleFocusIn(event: FocusEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        closeDropdown()
      }
    }

    document.addEventListener('focusin', handleFocusIn)
    return () => document.removeEventListener('focusin', handleFocusIn)
  }, [isOpen, closeDropdown])

  useEffect(() => {
    if (!isOpen || activeIndex < 0) return
    const optionEl = document.getElementById(`${baseId}-option-${activeIndex}`)
    optionEl?.scrollIntoView({ block: 'nearest' })
  }, [isOpen, activeIndex, baseId])

  const activeDescendant = isOpen && activeIndex >= 0
    ? `${baseId}-option-${activeIndex}`
    : undefined

  return (
    <>
      <S.SelectWrapper ref={wrapperRef}>
        {label ? (
          <Paragraph typo="paragraphXS" as="label" htmlFor={triggerId}>
            {label + (props?.required ? ' *' : '')}
          </Paragraph>
        ) : null}
        <S.SelectTrigger
          ref={triggerRef}
          id={triggerId}
          type="button"
          $isOpen={isOpen}
          $hasValue={!!selectedOption}
          $hasError={hasError}
          $disabled={disabled}
          colorScheme={selectedColorScheme}
          withBackground={withBackground}
          onClick={() => !disabled && (isOpen ? closeDropdown() : openDropdown())}
          onBlur={() => helpers.setTouched(true)}
          onKeyDown={handleTriggerKeyDown}
          aria-expanded={isOpen}
          aria-controls={listId}
          aria-haspopup="listbox"
          aria-activedescendant={activeDescendant}
          aria-invalid={hasError}
          aria-describedby={hasError ? errorId : undefined}
          aria-disabled={disabled}
          aria-required={props.required}
        >
          <span>{selectedOption ? selectedOption.label : placeholder}</span>
          <Icon name="chevron-down" size="small" aria-hidden />
        </S.SelectTrigger>

        {isOpen && (
          <S.SelectDropdown id={listId} role="listbox" aria-label={label}>
            {options.map((option, index) => {
              const isSelected = field.value === option.value
              const isActive = index === activeIndex
              return (
                <S.SelectOption
                  key={option.value}
                  id={`${baseId}-option-${index}`}
                  role="option"
                  $isSelected={isSelected}
                  $isActive={isActive}
                  aria-selected={isSelected}
                  onClick={() => handleSelect(option.value)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <Paragraph typo="paragraphMD" as="span">{option.label}</Paragraph>
                  {isSelected && (
                    <S.SelectCheckmark>
                      <Icon name="check" size="small" aria-hidden />
                    </S.SelectCheckmark>
                  )}
                </S.SelectOption>
              )
            })}
          </S.SelectDropdown>
        )}
      </S.SelectWrapper>

      {helperText && !hasError ? (
        <S.SelectHelperText colorScheme={selectedColorScheme} withBackground={withBackground}>
          <SpacerBase xs={1} />
          {helperText}
        </S.SelectHelperText>
      ) : null}

      {hasError ? (
        <S.SelectError id={errorId} role="alert">
          <SpacerBase xs={1} />
          {meta.error}
        </S.SelectError>
      ) : null}
    </>
  )
}

export default Select
